import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const Services = () => {
  const services = [
    {
      id: "eco-wash",
      title: "ECO Wash Package",
      description: "Our innovative \"Waterless Exterior Hand Wash\" removes all dirt, leaving your paint with a streak-free & wax-like finish, and protects your paint.",
      includes: [
        "Waterless Exterior Hand Wash",
        "Vacuum Interior",
        "Wipe down of Dashboard",
        "Clean windows inside and out",
        "Clean mirrors and door jams"
      ],
      price: {
        regular: 40,
        suv: 50
      },
      image: "/images/services/eco-wash.jpg"
    },
    {
      id: "complete-detail",
      title: "Complete Detail Package",
      description: "Complete Detail Package is the ultimate option for your car. This package includes every needed step to make your car shine inside & out again.",
      includes: [
        "Includes ECO Wash",
        "Includes ECO Wax",
        "Includes Interior Detail",
        "Includes Custom Polish",
        "Exterior Speed Polish"
      ],
      price: {
        regular: 300,
        suv: 350
      },
      image: "/images/services/complete-detail.jpg"
    },
    {
      id: "interior-detail",
      title: "Interior Detail Package",
      description: "Keeping the interior like new is just as important as the exterior of your car. We use steam, odor eliminators and only top of the line products throughout cleaning.",
      includes: [
        "Includes ECO Wash",
        "Cup holders, vents & doors",
        "Floor mats, carpet & seats",
        "Trunk & Ceiling",
        "Condition & Renew Leather Treatment"
      ],
      price: {
        regular: 170,
        suv: 190
      },
      image: "/images/services/interior-detail.jpg"
    },
    {
      id: "eco-wax",
      title: "ECO Wax Package",
      description: "Applying car wax regularly will extend the life and beauty of your vehicle's paint finish. We use only top of the line WAX products to make sure your car is safe.",
      includes: [
        "Includes ECO Wash",
        "Hand-applied premium wax",
        "Enhanced paint protection",
        "Improved shine and gloss",
        "Protection against environmental contaminants"
      ],
      price: {
        regular: 80,
        suv: 100
      },
      image: "/images/services/eco-wax.jpg"
    },
    {
      id: "exterior-detailing",
      title: "Exterior Detailing Package",
      description: "Hand polishing is a great way to shine your car in between details. It is used to remove grease, dirt, and scratches that cannot be removed from washing.",
      includes: [
        "Includes ECO Wash",
        "Hand polish with premium compounds",
        "Paint decontamination",
        "Minor scratch removal",
        "Enhanced gloss and depth"
      ],
      price: {
        regular: 190,
        suv: 210
      },
      image: "/images/services/exterior-detailing.jpg"
    },
    {
      id: "ceramic-coatings",
      title: "Ceramic Coatings",
      description: "Ceramic coating is a complex multi-stage process that protects from external paint damage and offers a better protection from scratches, dust and rust.",
      includes: [
        "Complete paint decontamination",
        "Paint correction (as needed)",
        "Professional-grade ceramic coating",
        "3-5 years of protection",
        "Enhanced gloss and hydrophobic properties"
      ],
      price: {
        regular: 1000,
        suv: 1200
      },
      image: "/images/services/ceramic-coating.jpg"
    },
    {
      id: "wheel-restoration",
      title: "Wheel Restoration",
      description: "We provide complete wheel restoration & painting solution to allow damaged wheels that are scratched, scraped or have peeling paint to look like new again.",
      includes: [
        "Wheel removal (if necessary)",
        "Complete cleaning and preparation",
        "Repair of curb rash and damage",
        "Professional refinishing",
        "Protective coating application"
      ],
      price: {
        perWheel: 125
      },
      image: "/images/services/wheel-restoration.jpg"
    },
    {
      id: "headlight-restoration",
      title: "Headlight Restoration",
      description: "By applying advanced wet sanding techniques, headlight restoration service removes dull, yellowed headlight build up and oxidation.",
      includes: [
        "Multi-stage wet sanding",
        "Professional polishing",
        "UV sealant application",
        "Improved nighttime visibility",
        "Enhanced appearance"
      ],
      price: {
        flat: 100
      },
      image: "/images/services/headlight-restoration.jpg"
    }
  ];

  // Helper to calculate competitor price (20% higher)
  const competitorPrice = (price: number) => Math.round(price * 1.2);

  // Structured data for services
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AutoRepair",
    "name": "Mobile Detail Co.",
    "description": "Professional mobile auto detailing services covering Minneapolis, St. Paul, and surrounding suburbs.",
    "image": "https://mobiledetailco.com/og-image.png",
    "telephone": "",
    "areaServed": [
      {
        "@type": "City",
        "name": "Minneapolis"
      },
      {
        "@type": "City",
        "name": "St. Paul"
      },
      {
        "@type": "AdministrativeArea",
        "name": "MN"
      }
    ],
    "makesOffer": services.map(service => ({
      "@type": "Offer",
      "itemOffered": {
        "@type": "Service",
        "name": service.title,
        "description": service.description
      }
    }))
  };

  return (
    <>
      <SEO
        title="Services & Prices | Mobile Detail Co."
        description="Professional auto detailing services in Twin Cities. From ECO Wash to Ceramic Coatings, we offer comprehensive car detailing packages at prices 20% lower than competitors."
        url="https://mobiledetailco.com/services"
        jsonLd={structuredData}
      />

      <div className="min-h-screen pt-24 bg-gradient-to-b from-white to-primary-light">
        {/* Hero Banner */}
        <div className="w-full bg-white py-16">
          <div className="container mx-auto px-4">
            <h1 className="text-4xl md:text-5xl font-bold text-primary text-center">
              Full Services & Pricing Comparison
            </h1>
            <p className="text-xl text-primary text-center mt-4">
              See how we stack up against the competition. Prices are starting points and may vary depending on vehicle type and condition. Extremely dirty vehicles may incur additional charges.
            </p>
          </div>
        </div>
        {/* Call to action */}
        <div className="container mx-auto px-4 py-8 text-center">
          <h3 className="text-2xl font-bold mb-4 text-primary">Call us today to schedule an appointment at (651) 286-9930</h3>
          <p className="text-primary mb-8">Mobile Services available at a minimum charge of $100. Prices listed below are for standard services.</p>
        </div>
        {/* Service Cards with Comparison Table */}
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div 
                key={service.id}
                id={service.id}
                className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:shadow-lg"
              >
                {/* Service Image Placeholder */}
                <div className="h-48 bg-gray-200">
                  {/* If you have actual images, replace this div with an image tag */}
                </div>
                <div className="p-6">
                  <h2 className="text-xl font-bold text-primary mb-2">
                    {service.title}
                  </h2>
                  <p className="text-gray-700 mb-4">
                    {service.description}
                  </p>
                  {/* Price Comparison Table */}
                  <div className="mb-4">
                    <table className="w-full text-sm border border-gray-200 mb-2">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="py-1 px-2 text-left">Type</th>
                          <th className="py-1 px-2 text-left">Competitor Price</th>
                          <th className="py-1 px-2 text-left">Our Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {service.price && service.price.regular && (
                          <tr>
                            <td className="py-1 px-2">Regular</td>
                            <td className="py-1 px-2 text-red-600 font-semibold">${competitorPrice(service.price.regular)}</td>
                            <td className="py-1 px-2 text-green-700 font-bold">${service.price.regular}</td>
                          </tr>
                        )}
                        {service.price && service.price.suv && (
                          <tr>
                            <td className="py-1 px-2">SUV</td>
                            <td className="py-1 px-2 text-red-600 font-semibold">${competitorPrice(service.price.suv)}</td>
                            <td className="py-1 px-2 text-green-700 font-bold">${service.price.suv}</td>
                          </tr>
                        )}
                        {service.price && service.price.perWheel && (
                          <tr>
                            <td className="py-1 px-2">Per Wheel</td>
                            <td className="py-1 px-2 text-red-600 font-semibold">${competitorPrice(service.price.perWheel)}</td>
                            <td className="py-1 px-2 text-green-700 font-bold">${service.price.perWheel}</td>
                          </tr>
                        )}
                        {service.price && service.price.flat && (
                          <tr>
                            <td className="py-1 px-2">Flat</td>
                            <td className="py-1 px-2 text-red-600 font-semibold">${competitorPrice(service.price.flat)}</td>
                            <td className="py-1 px-2 text-green-700 font-bold">${service.price.flat}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <div className="text-xs text-gray-500">Prices are starting points and may vary depending on vehicle type and condition. Extremely dirty vehicles may incur additional charges.</div>
                  </div>
                  <div className="mb-4">
                    <h3 className="font-semibold mb-2">Includes:</h3>
                    <ul className="list-disc pl-5 text-gray-700">
                      {service.includes.map((item, index) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                  </div>
                  <Button className="w-full" asChild>
                    <Link to="/booking">Book Now</Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 text-center text-gray-600 text-sm">
            * Price for some services may vary. Please, call (651) 286-9930 to get a precise quote.<br />
            <span className="text-xs text-gray-500">Competitor prices are estimated based on market averages and may vary by provider.</span>
          </div>
        </div>
        {/* Mobile Service CTA */}
        <div className="bg-gray-50 py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-primary mb-6">
              We Come To You!
            </h2>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              Our mobile detailing service brings the expertise and equipment right to your location. 
              Home, office, or anywhere convenient for you.
            </p>
            <Button size="lg" asChild>
              <Link to="/booking">Schedule Mobile Service</Link>
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Services;
